import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaTruckMoving,
  FaWarehouse,
  FaGlobe,
  FaAward,
  FaUsers,
  FaChartLine,
  FaRocket,
  FaHandshake,
  FaArrowLeft,
} from "react-icons/fa";
import logo1 from "../Images/para.png";

const milestones = [
  {
    year: "1986",
    title: "The Beginning",
    desc: "Founded with a single truck and a bold vision to become a trusted name in Indian road freight.",
    icon: <FaTruckMoving />,
  },
  {
    year: "1992",
    title: "First Major Contract",
    desc: "Secured our first enterprise-level transport contract, marking the beginning of industrial partnerships.",
    icon: <FaHandshake />,
  },
  {
    year: "2000",
    title: "Fleet Expansion",
    desc: "Grew fleet to 50+ vehicles and expanded operations across 5 states with dedicated route networks.",
    icon: <FaTruckMoving />,
  },
  {
    year: "2006",
    title: "Warehousing Launch",
    desc: "Opened our first warehousing facility with modern storage and inventory management capabilities.",
    icon: <FaWarehouse />,
  },
  {
    year: "2012",
    title: "Pan-India Reach",
    desc: "Expanded to 50+ cities, becoming a truly pan-India logistics provider with end-to-end solutions.",
    icon: <FaGlobe />,
  },
  {
    year: "2018",
    title: "Technology Integration",
    desc: "Implemented GPS tracking, digital documentation, and real-time shipment monitoring across the fleet.",
    icon: <FaChartLine />,
  },
  {
    year: "2025",
    title: "The Road Ahead",
    desc: "Expanding to 250+ fleet, launching AI-powered route optimization, and targeting 200+ cities coverage.",
    icon: <FaRocket />,
  },
];

function History() {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(null);

  return (
    <div className="hist-page">
      <div style={{ paddingTop: "68px" }}>
        {/* Hero */}
        <section className="hist-hero">
          <div className="hist-hero-bg" />
          <div className="hist-hero-content">
            <div className="hist-hero-badge">
              <img
                src={logo1}
                alt="Paras Roadlines"
                className="hist-hero-logo"
              />
            </div>
            <div className="hist-tag-row">
              <span className="hist-tag-line" />
              <span className="hist-tag">Our History</span>
              <span className="hist-tag-line" />
            </div>
            <h1>
              Built on Trust,
              <br />
              Driven by Excellence
            </h1>
            <p>
              From a single truck in 1986 to a fleet of 250+ vehicles across
              100+ cities — explore the journey that made Paras Roadlines one of
              India's most trusted logistics partners.
            </p>
            <div className="hist-hero-stats">
              <div className="hist-hstat">
                <span className="hist-hstat-num">39+</span>
                <span className="hist-hstat-lbl">Years of Legacy</span>
              </div>
              <div className="hist-hstat">
                <span className="hist-hstat-num">250+</span>
                <span className="hist-hstat-lbl">Fleet Vehicles</span>
              </div>
              <div className="hist-hstat">
                <span className="hist-hstat-num">100+</span>
                <span className="hist-hstat-lbl">Cities Served</span>
              </div>
              <div className="hist-hstat">
                <span className="hist-hstat-num">10K+</span>
                <span className="hist-hstat-lbl">Happy Clients</span>
              </div>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="hist-intro-sec">
          <div className="hist-container">
            <div className="hist-intro-grid">
              <div className="hist-intro-img-wrap">
                <div className="hist-intro-img">
                  <img
                    src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=800&q=80"
                    alt="Fleet"
                  />
                  <div className="hist-intro-img-badge">
                    <span>Est.</span>
                    <strong>1986</strong>
                  </div>
                </div>
              </div>
              <div className="hist-intro-text">
                <span className="hist-mini-tag">Who We Are</span>
                <h2>A Legacy of Reliable Road Transport</h2>
                <p>
                  What started as a small family-run trucking business in Bhilai
                  has grown into a comprehensive logistics enterprise spanning
                  the nation. Our founder's vision of making freight movement
                  safe, transparent, and efficient continues to guide every
                  decision we make.
                </p>
                <p>
                  Today, Paras Roadlines operates with a modern fleet equipped
                  with GPS tracking, a network of warehouses, and a dedicated
                  team committed to delivering cargo on time — every time.
                </p>
                <div className="hist-intro-highlights">
                  <div className="hist-highlight">
                    <div className="hist-highlight-icon">
                      <FaTruckMoving />
                    </div>
                    <div>
                      <strong>Modern Fleet</strong>
                      <span>GPS-enabled vehicles</span>
                    </div>
                  </div>
                  <div className="hist-highlight">
                    <div className="hist-highlight-icon">
                      <FaGlobe />
                    </div>
                    <div>
                      <strong>Pan-India Network</strong>
                      <span>100+ cities connected</span>
                    </div>
                  </div>
                  <div className="hist-highlight">
                    <div className="hist-highlight-icon">
                      <FaUsers />
                    </div>
                    <div>
                      <strong>Expert Team</strong>
                      <span>500+ professionals</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Horizontal Timeline */}
        <section className="hist-timeline-horiz-sec">
          <div className="hist-timeline-full">
            <div className="hist-sec-hdr">
              <div className="hist-tag-row">
                <span className="hist-tag-line" />
                <span className="hist-tag">Our Journey</span>
                <span className="hist-tag-line" />
              </div>
              <h2>Milestones That Define Us</h2>
              <p>
                Hover over any milestone to explore the moment that shaped our
                story
              </p>
            </div>

            <div className="hist-horiz-wrapper">
              <div className="hist-horiz-line" />
              <div className="hist-horiz-track">
                {milestones.map(({ year, title, desc, icon }, i) => {
                  const isFirst = i === 0;
                  const isLast = i === milestones.length - 1;
                  const isSecond = i === 1;
                  const isSecondLast = i === milestones.length - 2;

                  let tooltipAlign = "center";
                  if (isFirst) tooltipAlign = "left";
                  else if (isLast) tooltipAlign = "right";
                  else if (isSecond) tooltipAlign = "left-soft";
                  else if (isSecondLast) tooltipAlign = "right-soft";

                  return (
                    <div
                      className="hist-horiz-item"
                      key={year}
                      onMouseEnter={() => setActiveIndex(i)}
                      onMouseLeave={() => setActiveIndex(null)}
                    >
                      <div
                        className={`hist-horiz-dot ${activeIndex === i ? "active" : ""}`}
                      >
                        {icon}
                      </div>
                      <span className="hist-horiz-year">{year}</span>

                      {activeIndex === i && (
                        <div
                          className={`hist-horiz-tooltip hist-tooltip-${tooltipAlign}`}
                        >
                          <div
                            className={`hist-tooltip-arrow hist-arrow-${tooltipAlign}`}
                          />
                          <h4>{title}</h4>
                          <p>{desc}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      </div>

      <style>{`
        .hist-page{
          font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
          background:#ffffff;color:#101828;min-height:100vh;
        }
        .hist-container{max-width:1100px;margin:0 auto;padding:0 clamp(16px,3vw,40px);}
        .hist-timeline-full{max-width:1400px;margin:0 auto;padding:0 clamp(16px,3vw,48px);}

        /* Hero */
        .hist-hero{
          position:relative;
          background:linear-gradient(135deg,#101636 0%,#1a1f4e 100%);
          padding:clamp(60px,10vh,100px) 0 clamp(50px,8vh,80px);
          overflow:hidden;
          text-align:center;
        }
        .hist-hero-bg{
          position:absolute;inset:0;
          background-image:
            radial-gradient(ellipse at 20% 50%,rgba(79,70,229,.2) 0%,transparent 50%),
            radial-gradient(ellipse at 80% 50%,rgba(245,158,11,.1) 0%,transparent 50%),
            linear-gradient(rgba(255,255,255,.03) 1px,transparent 1px),
            linear-gradient(90deg,rgba(255,255,255,.03) 1px,transparent 1px);
          background-size:100% 100%,100% 100%,50px 50px,50px 50px;
        }
        .hist-hero-content{position:relative;z-index:2;max-width:700px;margin:0 auto;padding:0 20px;}
        .hist-hero-badge{
          width:80px;height:80px;margin:0 auto 1.5rem;
          background:rgba(255,255,255,.08);border:2px solid rgba(255,255,255,.15);
          border-radius:20px;display:flex;align-items:center;justify-content:center;
        }
        .hist-hero-logo{width:56px;height:56px;object-fit:contain;}
        .hist-hero h1{
          font-size:clamp(1.8rem,4vw,2.8rem);font-weight:900;color:#fff;
          line-height:1.15;letter-spacing:-1px;margin-bottom:1rem;
        }
        .hist-hero p{color:rgba(255,255,255,.65);font-size:.9rem;line-height:1.8;max-width:550px;margin:0 auto;}
        .hist-hero-stats{
          display:flex;justify-content:center;gap:0;margin-top:2.5rem;
          border-top:1px solid rgba(255,255,255,.1);padding-top:1.5rem;
        }
        .hist-hstat{text-align:center;padding:0 clamp(1rem,3vw,2.5rem);border-right:1px solid rgba(255,255,255,.1);}
        .hist-hstat:last-child{border-right:none;}
        .hist-hstat-num{display:block;font-size:clamp(1.2rem,2.5vw,1.6rem);font-weight:900;color:#fbbf24;}
        .hist-hstat-lbl{font-size:.58rem;color:rgba(255,255,255,.45);text-transform:uppercase;letter-spacing:1.5px;margin-top:3px;display:block;}

        /* Tags */
        .hist-tag-row{display:flex;align-items:center;justify-content:center;gap:14px;margin-bottom:1rem;}
        .hist-tag-line{width:40px;height:1px;background:rgba(255,255,255,.2);}
        .hist-tag{font-size:.6rem;font-weight:800;letter-spacing:3px;text-transform:uppercase;color:#fbbf24;}
        .hist-sec-hdr .hist-tag-line{background:#c7cbe8;}
        .hist-sec-hdr .hist-tag{color:#4f46e5;}

        /* Intro */
        .hist-intro-sec{padding:clamp(50px,8vh,90px) 0;}
        .hist-intro-grid{display:grid;grid-template-columns:1fr 1.2fr;gap:clamp(2rem,4vw,4rem);align-items:center;}
        .hist-intro-img-wrap{position:relative;}
        .hist-intro-img{position:relative;border-radius:20px;overflow:hidden;box-shadow:0 20px 50px rgba(16,24,40,.1);}
        .hist-intro-img img{width:100%;height:380px;object-fit:cover;display:block;}
        .hist-intro-img-badge{
          position:absolute;bottom:20px;right:20px;
          background:linear-gradient(135deg,#4f46e5,#4338ca);color:#fff;
          padding:12px 18px;border-radius:14px;text-align:center;
          box-shadow:0 8px 24px rgba(79,70,229,.35);
        }
        .hist-intro-img-badge span{display:block;font-size:.55rem;letter-spacing:2px;text-transform:uppercase;color:rgba(255,255,255,.7);}
        .hist-intro-img-badge strong{font-size:1.6rem;font-weight:900;letter-spacing:-1px;}
        .hist-mini-tag{
          display:inline-block;font-size:.6rem;font-weight:800;letter-spacing:2.5px;
          text-transform:uppercase;color:#4f46e5;background:#eef2ff;
          padding:6px 12px;border-radius:999px;margin-bottom:1rem;
        }
        .hist-intro-text h2{font-size:clamp(1.4rem,2.5vw,2rem);font-weight:900;color:#101828;letter-spacing:-.5px;margin-bottom:1rem;line-height:1.2;}
        .hist-intro-text p{color:#6b7280;font-size:.88rem;line-height:1.85;margin-bottom:1rem;}
        .hist-intro-highlights{display:flex;flex-direction:column;gap:.75rem;margin-top:1.5rem;}
        .hist-highlight{display:flex;align-items:center;gap:12px;background:#f8f9fc;border:1px solid #e6e8f0;border-radius:12px;padding:12px 16px;}
        .hist-highlight-icon{
          width:38px;height:38px;background:#eef2ff;border-radius:10px;
          display:flex;align-items:center;justify-content:center;flex-shrink:0;
        }
        .hist-highlight-icon svg{font-size:1rem;color:#4f46e5;}
        .hist-highlight strong{display:block;font-size:.82rem;font-weight:700;color:#101828;}
        .hist-highlight span{font-size:.68rem;color:#8b8fa3;}

        /* ══ HORIZONTAL TIMELINE ══ */
        .hist-timeline-horiz-sec{
          padding:clamp(50px,8vh,90px) 0;
          background:#f8f9fc;
          overflow:visible;
        }

        .hist-sec-hdr{text-align:center; margin-bottom:clamp(8rem,12vh,12rem);}
        .hist-sec-hdr h2{font-size:clamp(1.4rem,2.5vw,2.2rem);font-weight:900;color:#101828;letter-spacing:-.5px;margin-bottom:.5rem;}
        .hist-sec-hdr p{color:#6b7280;font-size:.88rem;max-width:500px;margin:0 auto;}

        .hist-horiz-wrapper{
          position:relative;
          padding:3rem 0 2rem;
          overflow:visible;
        }

        .hist-horiz-line{
          position:absolute;
          top:68px;
          left:0;
          right:0;
          height:4px;
          background:linear-gradient(90deg,#d7dafc,#4f46e5 30%,#4f46e5 70%,#fbbf24);
          border-radius:2px;
        }

        .hist-horiz-track{
          display:flex;
          justify-content:space-between;
          position:relative;
          width:100%;
        }

        .hist-horiz-item{
          display:flex;
          flex-direction:column;
          align-items:center;
          position:relative;
          cursor:pointer;
          flex:1;
        }

        .hist-horiz-dot{
          width:56px;
          height:56px;
          background:#ffffff;
          border:3px solid #d7dafc;
          border-radius:50%;
          display:flex;
          align-items:center;
          justify-content:center;
          transition:all .3s;
          position:relative;
          z-index:2;
        }

        .hist-horiz-dot svg{
          font-size:1.1rem;
          color:#4f46e5;
          transition:color .3s;
        }

        .hist-horiz-dot.active{
          background:linear-gradient(135deg,#4f46e5,#4338ca);
          border-color:#4f46e5;
          transform:scale(1.15);
          box-shadow:0 6px 20px rgba(79,70,229,.35);
        }

        .hist-horiz-dot.active svg{
          color:#fff;
        }

        .hist-horiz-year{
          margin-top:.75rem;
          font-size:.8rem;
          font-weight:800;
          color:#4f46e5;
          letter-spacing:1px;
        }

        /* Tooltip - Center aligned (default) */
        .hist-horiz-tooltip{
          position:absolute;
          bottom:calc(100% + 20px);
          background:#ffffff;
          border:1px solid #e6e8f0;
          border-radius:12px;
          padding:.85rem 1rem;
          width:230px;
          box-shadow:0 12px 32px rgba(16,24,40,.12);
          z-index:10;
          text-align:left;
          animation:tooltipFade .25s ease;
        }

        /* Center */
        .hist-tooltip-center{
          left:50%;
          transform:translateX(-50%);
        }

        /* Left edge */
        .hist-tooltip-left{
          left:0;
          transform:translateX(0);
        }

        /* Left soft (second item) */
        .hist-tooltip-left-soft{
          left:50%;
          transform:translateX(-30%);
        }

        /* Right edge */
        .hist-tooltip-right{
          right:0;
          left:auto;
          transform:translateX(0);
        }

        /* Right soft (second last) */
        .hist-tooltip-right-soft{
          left:50%;
          transform:translateX(-70%);
        }

        @keyframes tooltipFade{
          from{opacity:0;transform-origin:center;filter:blur(2px);}
          to{opacity:1;filter:blur(0);}
        }

        /* Arrow positions */
        .hist-tooltip-arrow{
          position:absolute;
          bottom:-7px;
          width:12px;
          height:12px;
          background:#fff;
          border-right:1px solid #e6e8f0;
          border-bottom:1px solid #e6e8f0;
          transform:rotate(45deg);
        }

        .hist-arrow-center{
          left:50%;
          margin-left:-6px;
        }

        .hist-arrow-left{
          left:24px;
        }

        .hist-arrow-left-soft{
          left:30%;
        }

        .hist-arrow-right{
          right:24px;
          left:auto;
        }

        .hist-arrow-right-soft{
          left:70%;
        }

        .hist-horiz-tooltip h4{
          font-size:.85rem;
          font-weight:700;
          color:#101828;
          margin-bottom:.25rem;
        }

        .hist-horiz-tooltip p{
          font-size:.75rem;
          color:#6b7280;
          line-height:1.65;
          margin:0;
        }

        /* Responsive */
        @media(max-width:900px){
          .hist-intro-grid{grid-template-columns:1fr;gap:2rem;}
          .hist-intro-img img{height:280px;}
        }
        @media(max-width:1024px){
          .hist-horiz-track{
            flex-wrap:wrap;
            justify-content:center;
            gap:2.5rem 1.5rem;
          }
          .hist-horiz-item{flex:0 0 auto;min-width:100px;}
          .hist-horiz-line{display:none;}
          .hist-horiz-wrapper{padding:1rem 0 2rem;}
          .hist-horiz-tooltip{
            position:absolute;
            bottom:calc(100% + 16px);
          }
          .hist-tooltip-left,
          .hist-tooltip-left-soft,
          .hist-tooltip-right,
          .hist-tooltip-right-soft,
          .hist-tooltip-center{
            left:50%;
            right:auto;
            transform:translateX(-50%);
          }
          .hist-arrow-left,
          .hist-arrow-left-soft,
          .hist-arrow-right,
          .hist-arrow-right-soft,
          .hist-arrow-center{
            left:50%;
            right:auto;
            margin-left:-6px;
          }
        }
        @media(max-width:768px){
          .hist-horiz-tooltip{width:210px;padding:.75rem .85rem;}
          .hist-hstat{padding:0 1rem;}
        }
        @media(max-width:480px){
          .hist-hstat{width:50%;border-right:none;border-bottom:1px solid rgba(255,255,255,.08);padding:.75rem 0;}
          .hist-hero-stats{flex-wrap:wrap;}
          .hist-horiz-track{gap:2rem 1rem;}
          .hist-horiz-tooltip{width:190px;}
        }
      `}</style>
    </div>
  );
}

export default History;
